import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  TextField,
  Chip,
  IconButton,
  InputAdornment,
  Autocomplete,
  Alert,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Paper,
  Avatar,
  Badge,
  Tooltip,
  Radio,
  RadioGroup,
  Slider,
  LinearProgress
} from '@mui/material';
import DialogCloseButton from '../common/DialogCloseButton';
import {
  Close,
  ArrowBack,
  ArrowForward,
  Home,
  Construction,
  Search,
  Add,
  CheckCircle,
  LocationOn,
  AttachMoney,
  CalendarToday,
  PhotoCamera,
  Description,
  People,
  Store,
  TrendingUp,
  History,
  Star,
  Phone,
  Email,
  Send,
  Upload,
  FileUpload,
  AutoAwesome,
  Lightbulb,
  Schedule,
  Assessment
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import projectService from '../../services/projectService';
import aiService from '../../services/aiService';
import api from '../../services/api';
import LocationInput from '../common/LocationInput';
import { coarseLocation } from '../../utils/location';
import AIDescriptionField from '../common/AIDescriptionField';
import PostCreationFlow from './PostCreationFlow';
import { getCostGuide, formatRange } from '../../data/costGuide';
import {
  FULL_BRIEF_QUESTIONS,
  FUNDING_QUESTION,
  FULL_BRIEF_TEXT_FIELDS,
  QUICK_BRIEF_TEXT_FIELDS,
  answeredBriefCount,
  structuredBriefRows,
} from '../../utils/structuredBrief';

/**
 * Unified Project Creation Wizard
 * A single 4-step wizard with integrated AI features
 * Replaces both NewProjectCreationWizard and ProjectCreationWizard
 */

// Join a list into readable prose: ['A','B','C'] -> 'A, B & C'
const humanizeList = (items) => {
  const list = (items || []).map((s) => (s || '').trim()).filter(Boolean);
  if (list.length === 0) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} & ${list[list.length - 1]}`;
};

// The sub-type label of a "Category - Type" selection, resolving "Custom" to
// whatever the user typed.
const selectionLabel = (selection, customText) => {
  const type = String(selection || '').split(' - ').pop();
  if (type === 'Custom') return (customText || '').trim();
  return type;
};

// The single task/type string used for the payload, title and AI context.
const effectiveTaskType = (projectType, data) => {
  if (projectType === 'quick') {
    return data.specificTask === 'Custom'
      ? (data.customTaskText || '').trim()
      : data.specificTask;
  }
  return humanizeList((data.fullProjectSelections || []).map((s) => selectionLabel(s, data.customTaskText)));
};

// One chip-row question of the structured brief questionnaire. Clicking the
// selected chip again clears it — nothing here is ever mandatory.
const ChipQuestion = ({ label, options, value, onChange }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {options.map((opt) => (
        <Chip
          key={opt.value}
          label={opt.label}
          clickable
          color={value === opt.value ? 'primary' : 'default'}
          variant={value === opt.value ? 'filled' : 'outlined'}
          onClick={() => onChange(value === opt.value ? '' : opt.value)}
        />
      ))}
    </Box>
  </Box>
);

// Structured questionnaire answers — everything optional, chips over essays.
const emptyBriefDetails = {
  buildStage: '',
  standStatus: '',
  plansStatus: '',
  roomsSummary: '',
  finishLevel: '',
  materialsSupplier: '',
  measurements: '',
  siteNotes: ''
};

const UnifiedProjectWizard = ({ open, onClose, onComplete }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Project data
  const [projectType, setProjectType] = useState(null); // 'quick' or 'full'
  const [projectData, setProjectData] = useState({
    name: '',
    location: '',
    description: '',
    taskCategory: '',
    specificTask: '',
    fullProjectType: '', // derived composite string (kept in sync from selections)
    fullProjectSelections: [], // multi-select: ['Renovation - Kitchen Renovation', ...]
    customTaskText: '', // free text when a "Custom" option is chosen
    budget: '',
    budgetApproach: 'unsure', // 'specific', 'guidance', 'unsure' (default to unsure)
    currency: 'USD',
    timeline: '',
    timelineType: 'flexible',
    specificDate: '',
    photos: [],
    planFiles: [], // PDF house plans / drawings
    details: { ...emptyBriefDetails },
    priorityFactors: [],
    coversTravel: false,
    travelNotes: ''
  });

  // AI state — description suggestions are managed inside AIDescriptionField now
  const [aiLoading, setAiLoading] = useState(false);
  const [intentDetection, setIntentDetection] = useState(null);
  const [photoAnalysis, setPhotoAnalysis] = useState(null);
  const [timelinePrediction, setTimelinePrediction] = useState(null);

  // AI-composed brief for the Review step. The original note is kept so the
  // client can always restore their own words.
  const [composedBrief, setComposedBrief] = useState(null);
  const [composeLoading, setComposeLoading] = useState(false);
  const [originalNote, setOriginalNote] = useState('');
  
  // Natural language input for intent detection
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [showNaturalLanguageInput, setShowNaturalLanguageInput] = useState(false);
  
  // Created project ID for post-creation flow
  const [createdProjectId, setCreatedProjectId] = useState(null);
  const [showPostCreation, setShowPostCreation] = useState(false);
  const [photoUploadFailed, setPhotoUploadFailed] = useState(false);

  const photoPreviews = useMemo(() => (
    projectData.photos.map((file) => ({
      file,
      url: URL.createObjectURL(file)
    }))
  ), [projectData.photos]);

  useEffect(() => {
    return () => {
      photoPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [photoPreviews]);
  
  // Task categories for Quick Tasks
  const taskCategories = {
    'Painting': ['Interior Painting', 'Exterior Painting', 'Roof Painting', 'Wall Painting', 'Custom'],
    'Plumbing': ['Pipe Repair', 'Tap Installation', 'Toilet Repair', 'Water Heater', 'Drainage', 'Custom'],
    'Electrical': ['Wiring', 'Socket Installation', 'Light Fixtures', 'Electrical Repair', 'Custom'],
    'Roofing': ['Roof Repair', 'Roof Replacement', 'Gutter Installation', 'Leak Repair', 'Custom'],
    'Walls': ['Wall Construction', 'Wall Repair', 'Plastering', 'Tiling', 'Custom'],
    'Doors/Windows': ['Door Installation', 'Window Installation', 'Door Repair', 'Window Repair', 'Custom'],
    'Outdoor': ['Landscaping', 'Paving', 'Fence Construction', 'Gate Installation', 'Custom'],
    'Maintenance': ['General Maintenance', 'Inspection', 'Cleaning', 'Repairs', 'Custom']
  };
  
  // Full project types
  const fullProjectTypes = {
    'New Construction': ['3-Bedroom House', '4-Bedroom House', 'Cottage', 'Apartment Building', 'Custom'],
    'Renovation': ['Full House Renovation', 'Kitchen Renovation', 'Bathroom Renovation', 'Room Addition', 'Painting', 'Custom'],
    'Complete Upgrade': ['Modernization', 'Extension', 'Conversion', 'Custom'],
    'Commercial': ['Office Building', 'Retail Space', 'Warehouse', 'Restaurant', 'Custom']
  };
  
  // Timeline options
  const timelineOptions = [
    { value: 'asap', label: 'ASAP' },
    { value: '1month', label: 'Within 1 Month' },
    { value: '3months', label: 'Within 3 Months' },
    { value: 'flexible', label: 'Flexible' },
    { value: 'specific', label: 'Specific Date' }
  ];
  
  // Priority factors
  const priorityFactorOptions = ['Quality', 'Speed', 'Cost', 'Sustainability'];

  const getCoordinatePair = (location) => {
    const coordinates = location?.coordinates || location?.coords;
    const lat = coordinates?.lat ?? coordinates?.latitude;
    const lng = coordinates?.lng ?? coordinates?.longitude;
    return { lat, lng };
  };

  const hasCoordinatePair = (location) => {
    const { lat, lng } = getCoordinatePair(location);
    return Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
  };

  const formatLocationValue = (location) => {
    if (typeof location === 'string') {
      return location.trim();
    }

    if (!location) {
      return '';
    }

    if (location.address?.trim()) {
      return location.address.trim();
    }

    if (hasCoordinatePair(location)) {
      const { lat, lng } = getCoordinatePair(location);
      return `Coordinates: ${Number(lat)}, ${Number(lng)}`;
    }

    return location.name?.trim() || '';
  };

  const RequiredMark = () => (
    <Box component="span" sx={{ color: 'error.main' }}>
      *
    </Box>
  );

  const setDetail = (key, value) => setProjectData(prev => ({
    ...prev,
    details: { ...(prev.details || {}), [key]: value }
  }));
  
  // AI budget estimation removed from the wizard (owner decision, Sept 2026):
  // not enough local pricing data yet for ranges we can stand behind. The
  // static costGuide anchor below remains.

  // Auto-save draft. File objects (photos/plans) don't survive JSON — strip
  // them so a restored draft never renders broken previews.
  // Visitors can describe the project before they have an account (the
  // /start page): their draft lives under a guest key and is adopted by the
  // account they create at the end.
  const GUEST_DRAFT_KEY = 'project_draft_guest';
  const draftKeyFor = (u) => (u?.id ? `project_draft_${u.id}` : GUEST_DRAFT_KEY);

  useEffect(() => {
    if (open && projectData.description) {
      const draftKey = draftKeyFor(user);
      localStorage.setItem(draftKey, JSON.stringify({
        projectType,
        projectData: { ...projectData, photos: [], planFiles: [] },
        timestamp: new Date().toISOString()
      }));
    }
  }, [projectData, projectType, open, user]);

  // Load draft on mount. A signed-in user with a guest draft (they described
  // the project first, then registered) picks it up, lands on Review, and the
  // guest copy is retired.
  useEffect(() => {
    if (open) {
      let draft = null;
      let fromGuest = false;
      try {
        if (user?.id) {
          const guest = localStorage.getItem(GUEST_DRAFT_KEY);
          if (guest) { draft = guest; fromGuest = true; localStorage.removeItem(GUEST_DRAFT_KEY); localStorage.setItem(draftKeyFor(user), guest); }
        }
        if (!draft) draft = localStorage.getItem(draftKeyFor(user));
      } catch (_) { /* storage unavailable */ }
      if (draft) {
        try {
          const parsed = JSON.parse(draft);
          const draftAge = Date.now() - new Date(parsed.timestamp).getTime();
          // Only load draft if less than 24 hours old (7 days for a guest draft
          // that has just been claimed — email verification can take a while).
          if (draftAge < (fromGuest ? 7 : 1) * 24 * 60 * 60 * 1000) {
            setProjectType(parsed.projectType);
            setProjectData(prev => ({
              ...prev,
              ...parsed.projectData,
              details: { ...emptyBriefDetails, ...(parsed.projectData?.details || {}) },
              photos: [],
              planFiles: []
            }));
            if (fromGuest && parsed.projectType && parsed.projectData?.description) setActiveStep(3);
          }
        } catch (e) {
          console.error('Failed to load draft:', e);
        }
      }
    }
  }, [open, user]);
  
  // Keep the derived composite fullProjectType in sync with the multi-select
  // choices so the payload, review screen and AI context all see one string.
  useEffect(() => {
    if (projectType !== 'full') return;
    setProjectData(prev => {
      const composed = humanizeList(
        (prev.fullProjectSelections || []).map(s => selectionLabel(s, prev.customTaskText))
      );
      return composed === prev.fullProjectType ? prev : { ...prev, fullProjectType: composed };
    });
  }, [projectType, projectData.fullProjectSelections, projectData.customTaskText]);

  // Auto-suggest the project title from the selections (only until the user
  // types their own). Fires as soon as work is picked — location is optional.
  const [titleManuallySet, setTitleManuallySet] = useState(false);
  useEffect(() => {
    if (titleManuallySet || !projectType) return;
    const base = effectiveTaskType(projectType, projectData);
    if (!base) return;
    const locationStr = typeof projectData.location === 'string'
      ? projectData.location
      : projectData.location?.address || '';
    // Use a COARSE location (suburb/city) in the title, never the exact street
    // address — the title is shown publicly to all browsing contractors.
    const coarse = coarseLocation(locationStr);
    const name = coarse ? `${base} - ${coarse}` : base;
    if (name !== projectData.name) {
      setProjectData(prev => ({ ...prev, name }));
    }
  }, [projectType, projectData.specificTask, projectData.fullProjectType, projectData.customTaskText, projectData.location, titleManuallySet]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Step definitions
  const steps = ['What Are You Building?', 'Share Your Brief', 'Budget & Timing', 'Review & Create'];
  
  // Handle next step
  const handleNext = () => {
    // Validation
    if (activeStep === 0 && !projectType) {
      setError('Please select a project type');
      return;
    }
    
    if (activeStep === 1) {
      if (projectType === 'quick' && !projectData.specificTask) {
        setError('Please select a task');
        return;
      }
      if (projectType === 'full' && (projectData.fullProjectSelections || []).length === 0) {
        setError('Please select at least one type of work');
        return;
      }
      if (
        projectData.specificTask === 'Custom' ||
        (projectData.fullProjectSelections || []).some((s) => s.endsWith(' - Custom'))
      ) {
        if (!projectData.customTaskText?.trim()) {
          setError('Please describe the custom work you selected');
          return;
        }
      }
      if (!projectData.name?.trim()) {
        setError('Please enter a project title');
        return;
      }
      if (!formatLocationValue(projectData.location) || !projectData.description?.trim()) {
        setError('Please fill in all required fields');
        return;
      }
    }
    
    if (activeStep === 2) {
      // Budget is now optional; no validation required.
      // Kick off the AI brief composition so the Review step opens with a
      // contractor-ready draft (non-blocking; review works without it).
      triggerComposeBrief();
    }

    if (activeStep === 3) {
      if (!user) {
        // Guest: the brief is saved under the guest key; create the account,
        // come back to /projects?create=1 and the wizard reopens on Review.
        try {
          localStorage.setItem(GUEST_DRAFT_KEY, JSON.stringify({
            projectType,
            projectData: { ...projectData, photos: [], planFiles: [] },
            timestamp: new Date().toISOString()
          }));
        } catch (_) { /* storage unavailable */ }
        navigate(`/register?role=client&next=${encodeURIComponent('/projects?create=1')}`);
        return;
      }
      // Create project
      handleCreateProject();
      return;
    }

    setError('');
    setActiveStep(prev => prev + 1);
  };

  // Collect the non-empty structured answers for the payload / AI composer.
  const collectStructuredBrief = () => {
    const details = projectData.details || {};
    const brief = { version: 1 };
    Object.entries(details).forEach(([key, value]) => {
      if (String(value || '').trim()) brief[key] = String(value).trim();
    });
    if (projectData.timelineType) {
      brief.timelineType = projectData.timelineType;
      if (projectData.timelineType === 'specific' && projectData.specificDate) {
        brief.startDate = projectData.specificDate;
      }
    }
    if ((projectData.priorityFactors || []).length > 0) {
      brief.priorities = projectData.priorityFactors;
    }
    return brief;
  };

  // AI: compose the contractor-facing brief from the rough note + structured
  // answers. If the client's own note is short, the composed brief becomes the
  // editable default (their original words are kept and restorable); a longer,
  // clearly effortful note is never overwritten — the brief is offered as a
  // suggestion instead.
  const triggerComposeBrief = async () => {
    if (composeLoading || composedBrief) return;
    setComposeLoading(true);
    try {
      const result = await aiService.composeBrief({
        projectType,
        taskType: effectiveTaskType(projectType, projectData),
        title: projectData.name,
        location: formatLocationValue(projectData.location),
        ownerNote: projectData.description,
        structuredBrief: collectStructuredBrief(),
        photoCount: projectData.photos.length,
        planCount: projectData.planFiles.length
      });
      const brief = result?.data?.brief || result?.brief;
      if (brief) {
        setComposedBrief(brief);
        setProjectData(prev => {
          const current = (prev.description || '').trim();
          if (current.length < 200) {
            setOriginalNote(prev.description);
            return { ...prev, description: brief };
          }
          return prev;
        });
      }
    } catch (e) {
      // Composition is best-effort — the review step simply shows the client's
      // own text if the AI is unavailable.
      console.warn('Brief composition unavailable:', e.message);
    } finally {
      setComposeLoading(false);
    }
  };
  
  // Handle back step
  const handleBack = () => {
    setError('');
    setActiveStep(prev => prev - 1);
  };
  
  // Handle close
  const handleClose = () => {
    setActiveStep(0);
    setProjectType(null);
    setProjectData({
      name: '',
      location: '',
      description: '',
      taskCategory: '',
      specificTask: '',
      fullProjectType: '',
      fullProjectSelections: [],
      customTaskText: '',
      budget: '',
      budgetApproach: 'unsure',
      currency: 'USD',
      timeline: '',
      timelineType: 'flexible',
      specificDate: '',
      photos: [],
      planFiles: [],
      details: { ...emptyBriefDetails },
      priorityFactors: [],
      coversTravel: false,
      travelNotes: ''
    });
    setError('');
    setComposedBrief(null);
    setComposeLoading(false);
    setOriginalNote('');
    setCreatedProjectId(null);
    setShowPostCreation(false);
    onClose();
  };
  
  // AI: Detect intent from natural language
  const handleDetectIntent = async () => {
    if (!naturalLanguageInput.trim()) return;
    
    setAiLoading(true);
    try {
      const result = await aiService.detectIntent(naturalLanguageInput);
      if (result.success) {
        setIntentDetection(result.data);
        setProjectType(result.data.projectType);
        // Auto-fill some fields if possible
        if (result.data.suggestedCategory) {
          if (result.data.projectType === 'quick') {
            setProjectData(prev => ({ ...prev, taskCategory: result.data.suggestedCategory }));
          } else {
            setProjectData(prev => ({ ...prev, fullProjectType: result.data.suggestedCategory }));
          }
        }
      }
    } catch (error) {
      console.error('Intent detection failed:', error);
    } finally {
      setAiLoading(false);
    }
  };
  
  // AI: Analyze photo
  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    
    // Add photos to project data as reference images only.
    setProjectData(prev => ({
      ...prev,
      photos: [...prev.photos, ...files]
    }));

    // AI photo analysis is DISABLED for now: owners often upload inspiration/
    // reference photos of the result they WANT (e.g. a finished house), and the
    // model wrongly described them as the CURRENT site condition — misleading to
    // contractors. Photos are still uploaded as references; no analysis is run
    // and nothing is shown/stored. Re-enable by restoring the analyzePhoto call.
  };

  const handleRemovePhoto = (indexToRemove) => {
    setProjectData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, index) => index !== indexToRemove)
    }));
  };

  // House plans / drawings — PDFs only here (photos of plans belong with the
  // reference photos above, where every image type is accepted).
  const handlePlanUpload = (event) => {
    const files = Array.from(event.target.files).filter(
      (f) => f.type === 'application/pdf'
    );
    if (files.length === 0) return;
    setProjectData(prev => ({
      ...prev,
      planFiles: [...prev.planFiles, ...files]
    }));
  };

  const handleRemovePlan = (indexToRemove) => {
    setProjectData(prev => ({
      ...prev,
      planFiles: prev.planFiles.filter((_, index) => index !== indexToRemove)
    }));
  };

  const uploadReferencePhotos = async (projectId) => {
    if (!projectId || projectData.photos.length === 0) return;

    const formData = new FormData();
    projectData.photos.forEach(file => formData.append('files', file));
    formData.append('category', 'photo');
    formData.append('description', 'Owner reference photos from project brief');

    await api.post(`/projects/${projectId}/files`, formData);
  };

  const uploadPlanDocuments = async (projectId) => {
    if (!projectId || projectData.planFiles.length === 0) return;

    const formData = new FormData();
    projectData.planFiles.forEach(file => formData.append('files', file));
    formData.append('category', 'document');
    formData.append('description', 'Owner plans and drawings from project brief');

    await api.post(`/projects/${projectId}/files`, formData);
  };

  // One automatic retry before surfacing the failure to the user — the project
  // is already created at this point, so the client must know if their
  // reference photos/plans didn't attach.
  const uploadReferencePhotosWithRetry = async (projectId) => {
    let photosDone = false;
    const attempt = async () => {
      if (!photosDone) {
        await uploadReferencePhotos(projectId);
        photosDone = true; // don't re-upload (duplicate) photos if only the plans failed
      }
      await uploadPlanDocuments(projectId);
    };
    try {
      await attempt();
    } catch (firstError) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      await attempt();
    }
  };

  // AI: Predict timeline
  const handlePredictTimeline = async () => {
    setAiLoading(true);
    try {
      const result = await aiService.predictTimeline({
        projectType,
        taskType: effectiveTaskType(projectType, projectData),
        description: projectData.description,
        budget: projectData.budget || undefined,
        location: typeof projectData.location === 'string' ? projectData.location : projectData.location?.address
      });
      if (result.success) {
        setTimelinePrediction(result.data);
      }
    } catch (error) {
      console.error('Timeline prediction failed:', error);
    } finally {
      setAiLoading(false);
    }
  };
  
  // Create project
  const handleCreateProject = async () => {
    setLoading(true);
    setError('');
    
    try {
      const locationValue = formatLocationValue(projectData.location);
      
      // Persist the AI insights so contractors see them when bidding —
      // without this the analysis the client just watched is lost.
      // (No budgetEstimation any more: AI budget estimates are disabled.)
      const aiMetadata = {};
      if (intentDetection) aiMetadata.intentDetection = intentDetection;
      if (photoAnalysis) aiMetadata.photoAnalysis = photoAnalysis;
      if (timelinePrediction) aiMetadata.timelinePrediction = timelinePrediction;

      // Structured questionnaire answers + the client's own words before the
      // AI-composed brief (if it replaced them) — contractors see the answers
      // as a spec sheet.
      const structuredBrief = collectStructuredBrief();
      if (originalNote && originalNote.trim() && originalNote.trim() !== projectData.description.trim()) {
        structuredBrief.ownerNote = originalNote.trim();
      }

      const payload = {
        title: projectData.name,
        description: projectData.description,
        location: locationValue,
        budget: projectData.budgetApproach === 'unsure' || !projectData.budget ? 0 : parseFloat(projectData.budget),
        currency: projectData.currency,
        project_type: projectType,
        task_type: projectType === 'quick' ? effectiveTaskType('quick', projectData) : null,
        full_project_type: projectType === 'full' ? effectiveTaskType('full', projectData) : null,
        ai_metadata: Object.keys(aiMetadata).length > 0 ? aiMetadata : undefined,
        structured_brief: Object.keys(structuredBrief).length > 1 ? structuredBrief : undefined,
        covers_travel: projectData.coversTravel,
        travel_notes: projectData.coversTravel ? (projectData.travelNotes || null) : null,
        status: 'open_for_bids'
      };

      const result = await projectService.createProject(user.id, payload);

      if (result.success) {
        const projectId = result.data?.id || result.project?.id;
        try {
          await uploadReferencePhotosWithRetry(projectId);
        } catch (uploadError) {
          console.warn('Project created, but reference photo upload failed:', uploadError);
          setPhotoUploadFailed(true);
        }
        setCreatedProjectId(projectId);
        setShowPostCreation(true);
        
        // Clear draft
        localStorage.removeItem(`project_draft_${user.id}`);
        
        if (onComplete) {
          onComplete(projectId);
        }
      } else {
        setError(result.error || 'Failed to create project');
      }
    } catch (err) {
      console.error('Error creating project:', err);
      setError(err.message || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  // Continue with rendering functions...
  

  // Render Step 1: What Are You Building?
  const renderStep1 = () => {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
          What Are You Building?
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
          Choose the type that best matches your needs
        </Typography>

        {/* Popular project templates for first-time users */}
        {!projectType && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 1.5, display: 'block' }}>
              Popular with property owners
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                { label: 'Paint a room', type: 'quick', category: 'Painting', task: 'Interior Painting' },
                { label: 'Fix a leaking pipe', type: 'quick', category: 'Plumbing', task: 'Pipe Repair' },
                { label: 'Install lights', type: 'quick', category: 'Electrical', task: 'Light Fixtures' },
                { label: 'Repair my roof', type: 'quick', category: 'Roofing', task: 'Roof Repair' },
                { label: 'Build a 3-bedroom house', type: 'full', category: 'New Construction', task: '3-Bedroom House' },
                { label: 'Renovate my kitchen', type: 'full', category: 'Renovation', task: 'Kitchen Renovation' },
                { label: 'Build a boundary wall', type: 'quick', category: 'Walls', task: 'Wall Construction' },
                { label: 'Install a gate', type: 'quick', category: 'Outdoor', task: 'Gate Installation' },
              ].map((template) => (
                <Chip
                  key={template.label}
                  label={template.label}
                  variant="outlined"
                  clickable
                  onClick={() => {
                    setProjectType(template.type);
                    setProjectData(prev => ({
                      ...prev,
                      taskCategory: template.type === 'quick' ? template.category : '',
                      specificTask: template.type === 'quick' ? template.task : '',
                      fullProjectSelections: template.type === 'full' ? [`${template.category} - ${template.task}`] : [],
                      fullProjectType: template.type === 'full' ? template.task : '',
                    }));
                    setError('');
                    setActiveStep(1);
                  }}
                  sx={{
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Optional: Natural Language Input with AI Intent Detection */}
        <Box sx={{ mb: 4 }}>
          <Button
            variant="outlined"
            startIcon={<AutoAwesome />}
            onClick={() => setShowNaturalLanguageInput(!showNaturalLanguageInput)}
            fullWidth
            sx={{ mb: 2 }}
          >
            {showNaturalLanguageInput ? 'Hide' : 'Describe Your Project in Your Own Words'}
          </Button>
          
          {showNaturalLanguageInput && (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., I want to build a 3-bedroom house in Harare"
                value={naturalLanguageInput}
                onChange={(e) => setNaturalLanguageInput(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                startIcon={<Lightbulb />}
                onClick={handleDetectIntent}
                disabled={!naturalLanguageInput.trim() || aiLoading}
                fullWidth
              >
                {aiLoading ? <CircularProgress size={24} /> : 'Detect Project Type with AI'}
              </Button>
              
              {intentDetection && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    AI Suggestion: {intentDetection.projectType === 'quick' ? 'Quick Task' : 'Full Project'}
                  </Typography>
                  <Typography variant="caption">
                    {intentDetection.reasoning}
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </Box>
        
        {/* Project Type Selection Cards */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: 'pointer',
                border: projectType === 'quick' ? 2 : 1,
                borderColor: projectType === 'quick' ? 'primary.main' : 'grey.300',
                bgcolor: projectType === 'quick' ? 'primary.50' : 'white',
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => {
                setProjectType('quick');
                setError('');
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h3" sx={{ mb: 2 }}>🔨</Typography>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Quick Task
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Single task jobs that are finished in one go
                </Typography>
                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>• Painting</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>• Plumbing repair</Typography>
                  <Typography variant="body2">• Wall construction</Typography>
                </Box>
                {projectType === 'quick' && (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 3 }}
                    endIcon={<ArrowForward />}
                  >
                    Selected
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                cursor: 'pointer',
                border: projectType === 'full' ? 2 : 1,
                borderColor: projectType === 'full' ? 'primary.main' : 'grey.300',
                bgcolor: projectType === 'full' ? 'primary.50' : 'white',
                transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => {
                setProjectType('full');
                setError('');
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h3" sx={{ mb: 2 }}>🏗️</Typography>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Full Project
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Multi-phase builds with multiple stages
                </Typography>
                <Box sx={{ textAlign: 'left', mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>• Building a house</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>• Major renovations</Typography>
                  <Typography variant="body2">• Commercial construction</Typography>
                </Box>
                {projectType === 'full' && (
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ mt: 3 }}
                    endIcon={<ArrowForward />}
                  >
                    Selected
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };

  // Render Step 2: Project Details
  const renderStep2 = () => {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
          Tell Us About Your {projectType === 'quick' ? 'Task' : 'Project'}
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Use plain language and reference photos. Contractors will turn this into a work plan, materials schedule, and milestones.
        </Typography>
        
        {/* Task/Project Type Selection */}
        {projectType === 'quick' && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              What type of task? *
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(taskCategories).map((category) => (
                <Grid item xs={6} sm={4} md={3} key={category}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: projectData.taskCategory === category ? 2 : 1,
                      borderColor: projectData.taskCategory === category ? 'primary.main' : 'grey.300',
                      bgcolor: projectData.taskCategory === category ? 'primary.50' : 'white',
                      transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        borderColor: 'primary.main',
                        transform: 'scale(1.02)'
                      }
                    }}
                    onClick={() => {
                      setProjectData(prev => ({ ...prev, taskCategory: category, specificTask: '' }));
                    }}
                  >
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {category}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            
            {projectData.taskCategory && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Specific Task *
                </Typography>
                <Grid container spacing={2}>
                  {taskCategories[projectData.taskCategory].map((task) => (
                    <Grid item xs={12} sm={6} key={task}>
                      <Button
                        variant={projectData.specificTask === task ? 'contained' : 'outlined'}
                        fullWidth
                        onClick={() => setProjectData(prev => ({ ...prev, specificTask: task }))}
                      >
                        {task}
                      </Button>
                    </Grid>
                  ))}
                </Grid>

                {projectData.specificTask === 'Custom' && (
                  <TextField
                    fullWidth
                    sx={{ mt: 2 }}
                    label="Describe the task"
                    placeholder="Tell contractors what you need done"
                    value={projectData.customTaskText}
                    onChange={(e) => setProjectData(prev => ({ ...prev, customTaskText: e.target.value }))}
                    required
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {projectType === 'full' && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              What type of work? <RequiredMark />{' '}
              <Typography component="span" variant="caption" color="text.secondary">
                (select all that apply)
              </Typography>
            </Typography>
            <Grid container spacing={2}>
              {Object.keys(fullProjectTypes).map((category) => {
                const categoryActive = (projectData.fullProjectSelections || [])
                  .some((s) => s.startsWith(`${category} - `));
                return (
                  <Grid item xs={12} sm={6} key={category}>
                    <Card
                      sx={{
                        border: categoryActive ? 2 : 1,
                        borderColor: categoryActive ? 'primary.main' : 'grey.300',
                        bgcolor: categoryActive ? 'primary.50' : 'white',
                        transition: 'border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': { borderColor: 'primary.main' }
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          {category}
                        </Typography>
                        {fullProjectTypes[category].map((type) => {
                          const value = `${category} - ${type}`;
                          const checked = (projectData.fullProjectSelections || []).includes(value);
                          return (
                            <FormControlLabel
                              key={type}
                              sx={{ display: 'flex', m: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={checked}
                                  onChange={() => setProjectData(prev => {
                                    const list = prev.fullProjectSelections || [];
                                    return {
                                      ...prev,
                                      fullProjectSelections: checked
                                        ? list.filter((v) => v !== value)
                                        : [...list, value],
                                    };
                                  })}
                                />
                              }
                              label={<Typography variant="body2">{type}</Typography>}
                            />
                          );
                        })}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            {(projectData.fullProjectSelections || []).some((s) => s.endsWith(' - Custom')) && (
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Describe the custom work"
                placeholder="Tell contractors what the custom work involves"
                value={projectData.customTaskText}
                onChange={(e) => setProjectData(prev => ({ ...prev, customTaskText: e.target.value }))}
                required
              />
            )}

            {projectData.fullProjectType && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                Selected: {projectData.fullProjectType}
              </Typography>
            )}
          </Box>
        )}

        {/* Structured details questionnaire — a few taps that turn a one-line
            brief into something a contractor can actually quote. */}
        {projectType && (
          <Paper variant="outlined" sx={{ p: 2.5, mb: 4, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              A few quick details
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              All optional — but every answer helps contractors quote accurately instead of guessing.
            </Typography>
            {projectType === 'full' && FULL_BRIEF_QUESTIONS.map((q) => (
              <ChipQuestion
                key={q.key}
                label={q.label}
                options={q.options}
                value={projectData.details?.[q.key] || ''}
                onChange={(v) => setDetail(q.key, v)}
              />
            ))}
            {/* Asked on every project: a staged quotation is the one thing
                that stops a big total from ending the conversation. */}
            <ChipQuestion
              label={FUNDING_QUESTION.label}
              options={FUNDING_QUESTION.options}
              value={projectData.details?.fundingPreference || ''}
              onChange={(v) => setDetail('fundingPreference', v)}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1.5, mb: 2.5 }}>
              Most clients choose stages: the contractor quotes the job stage by stage, you fund one stage at a time into your project wallet, and each is paid out only when you approve the finished work. You never have to find the whole amount at once.
            </Typography>
            {(projectType === 'full' ? FULL_BRIEF_TEXT_FIELDS : QUICK_BRIEF_TEXT_FIELDS).map((f) => (
              <TextField
                key={f.key}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                label={f.label}
                placeholder={f.placeholder}
                value={projectData.details?.[f.key] || ''}
                onChange={(e) => setDetail(f.key, e.target.value)}
              />
            ))}
          </Paper>
        )}

        {/* Project Title */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Project Title <RequiredMark />
          </Typography>
          <TextField
            fullWidth
            placeholder="Give your project a clear, descriptive title"
            value={projectData.name}
            onChange={(e) => {
              setTitleManuallySet(true);
              setProjectData(prev => ({ ...prev, name: e.target.value }));
            }}
            helperText="A good title helps contractors understand your project at a glance"
          />
        </Box>

        {/* Location */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Location <RequiredMark />
          </Typography>
          <LocationInput
            value={projectData.location}
            onChange={(value) => setProjectData(prev => ({ ...prev, location: value }))}
            placeholder="Enter project location"
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={projectData.coversTravel}
                  onChange={(e) => setProjectData(prev => ({ ...prev, coversTravel: e.target.checked }))}
                />
              }
              label="No contractors nearby? I'll cover reasonable travel/transport costs"
            />
            {projectData.coversTravel && (
              <TextField
                fullWidth
                size="small"
                placeholder="Optional: e.g. happy to cover fuel/bus fare; ~120 km from Harare"
                value={projectData.travelNotes}
                onChange={(e) => setProjectData(prev => ({ ...prev, travelNotes: e.target.value }))}
                inputProps={{ maxLength: 1000 }}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </Box>

        {/* Description with AI Auto-Complete — shared AIDescriptionField */}
        <Box sx={{ mb: 4 }}>
          <AIDescriptionField
            value={projectData.description}
            onChange={(value) => setProjectData(prev => ({ ...prev, description: value }))}
            label="What do you want done?"
            required
            placeholder="Example: I want to renovate my kitchen with new cabinets, tiles, plumbing fixtures, and better lighting. I have photos of the style I like."
            helperText="Do not worry about BOQs or construction stages. Contractors will propose those after reviewing your brief."
            projectType={projectType}
            taskType={effectiveTaskType(projectType, projectData)}
            location={projectData.location}
          />
        </Box>
        
        {/* Photo Upload with AI Analysis */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Reference Photos (Optional)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            Upload site photos, inspiration images, sketches, or anything that helps contractors understand what you mean.
          </Typography>
          
          <Button
            variant="outlined"
            component="label"
            startIcon={<PhotoCamera />}
            fullWidth
          >
            Upload Reference Photos
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handlePhotoUpload}
            />
          </Button>
          
          {projectData.photos.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {projectData.photos.length} photo(s) selected. Check the previews below before continuing.
              </Typography>
              <Grid container spacing={1.5}>
                {photoPreviews.map((preview, index) => (
                  <Grid item xs={6} sm={4} md={3} key={`${preview.file.name}-${index}`}>
                    <Paper
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: 1.5,
                        aspectRatio: '1 / 1',
                        bgcolor: 'grey.50'
                      }}
                    >
                      <Box
                        component="img"
                        src={preview.url}
                        alt={preview.file.name}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                      />
                      <IconButton
                        aria-label={`Remove ${preview.file.name}`}
                        size="small"
                        onClick={() => handleRemovePhoto(index)}
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          bgcolor: 'rgba(0,0,0,0.58)',
                          color: 'white',
                          '&:hover': { bgcolor: 'rgba(0,0,0,0.76)' }
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: 0,
                          px: 1,
                          py: 0.75,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {preview.file.name}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
          
          {/* House plans / drawings — the single most useful attachment for a
              contractor pricing a build. PDFs only; photos of paper plans go
              in the reference photos above. */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              House Plans / Drawings (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              {projectType === 'full'
                ? 'If you have plans or drawings, attach them — nothing helps a contractor quote a build more accurately.'
                : 'Attach any sketch, layout, or drawing that shows what you want done.'}
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<FileUpload />}
              fullWidth
            >
              Upload Plans (PDF)
              <input
                type="file"
                hidden
                multiple
                accept="application/pdf"
                onChange={handlePlanUpload}
              />
            </Button>
            {projectData.planFiles.length > 0 && (
              <List dense sx={{ mt: 1 }}>
                {projectData.planFiles.map((file, index) => (
                  <ListItem
                    key={`${file.name}-${index}`}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, mb: 0.75 }}
                    secondaryAction={
                      <IconButton edge="end" size="small" aria-label={`Remove ${file.name}`} onClick={() => handleRemovePlan(index)}>
                        <Close fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Description color="primary" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={`${Math.max(1, Math.round(file.size / 1024))} KB`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          {photoAnalysis && (
            <Alert severity="info" icon={<AutoAwesome />} sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                AI Photo Analysis
              </Typography>
              <Typography variant="caption">
                Detected: {photoAnalysis.detectedFeatures?.join(', ')}
              </Typography>
              <br />
              {photoAnalysis.scopeRelevantObservations?.length > 0 && (
                <>
                  <Typography variant="caption">
                    Relevant to your brief: {photoAnalysis.scopeRelevantObservations.join(', ')}
                  </Typography>
                  <br />
                </>
              )}
              <Typography variant="caption">
                Condition: {photoAnalysis.conditionAssessment}
              </Typography>
              <br />
              <Typography variant="caption">
                Complexity: {photoAnalysis.complexityLevel}
              </Typography>
            </Alert>
          )}
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };

  // Render Step 3: Budget & Timeline
  const renderStep3 = () => {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
          Budget & Timing
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
          A rough budget and preferred timing are enough. Contractors will propose detailed pricing and milestones.
        </Typography>
        
        {/* AI "Estimated Planning Range" removed (Sept 2026): not enough local
            pricing data yet to stand behind AI-generated budget ranges. The
            static costGuide anchor below stays. */}

        {/* Budget Section */}
        <Box sx={{ mb: 5 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Budget (optional)
          </Typography>

          {/* Indicative cost anchor so first-time owners know what jobs like
              this usually cost before quotes arrive */}
          {(() => {
            const guide = getCostGuide(projectData);
            if (!guide) return null;
            return (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'info.50', borderColor: 'info.light' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Typical range in Zimbabwe: {formatRange(guide)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Indicative only, based on similar {guide.matchedOn.toLowerCase()} work. Actual quotes depend on size, materials, and site conditions — you&apos;ll compare real quotations before committing to anything.
                </Typography>
              </Paper>
            );
          })()}

          <RadioGroup
            value={projectData.budgetApproach}
            onChange={(e) => setProjectData(prev => ({ ...prev, budgetApproach: e.target.value }))}
          >
            <FormControlLabel
              value="unsure"
              control={<Radio />}
              label="I'm not sure / I'll build as I go"
            />
            {/* "I need guidance (AI can estimate)" option hidden for now */}
            <FormControlLabel
              value="specific"
              control={<Radio />}
              label="I have a specific budget"
            />

          {projectData.budgetApproach === 'specific' && (
            <Box sx={{ ml: 4, mt: 2, mb: 3 }}>
              <TextField
                label="Budget Amount (optional)"
                type="number"
                value={projectData.budget}
                onChange={(e) => setProjectData(prev => ({ ...prev, budget: e.target.value }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                sx={{ width: 300 }}
              />
            </Box>
          )}

          </RadioGroup>
        </Box>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Timeline Section */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              When do you want to start?
            </Typography>
            <Button
              size="small"
              startIcon={<Schedule />}
              onClick={handlePredictTimeline}
              disabled={aiLoading}
            >
              AI Timeline Prediction
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            {timelineOptions.map((option) => (
              <Grid item xs={12} sm={6} md={4} key={option.value}>
                <Button
                  variant={projectData.timelineType === option.value ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={() => setProjectData(prev => ({ ...prev, timelineType: option.value }))}
                >
                  {option.label}
                </Button>
              </Grid>
            ))}
          </Grid>
          
          {projectData.timelineType === 'specific' && (
            <TextField
              type="date"
              label="Preferred Start Date"
              value={projectData.specificDate}
              onChange={(e) => setProjectData(prev => ({ ...prev, specificDate: e.target.value }))}
              sx={{ mt: 2, width: 300 }}
              InputLabelProps={{ shrink: true }}
            />
          )}
          
          {timelinePrediction && (
            <Paper sx={{ p: 3, mt: 3, bgcolor: 'info.50' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                AI Timeline Prediction
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Estimated Duration: {timelinePrediction.timelineRange.min} - {timelinePrediction.timelineRange.max} {timelinePrediction.timelineRange.unit}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Optimal Start Month: {timelinePrediction.optimalStartMonth}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {timelinePrediction.reasoning}
              </Typography>
              
              {timelinePrediction.seasonalConsiderations && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>Seasonal Note:</strong> {timelinePrediction.seasonalConsiderations}
                  </Typography>
                </Alert>
              )}
            </Paper>
          )}
        </Box>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Priority Factors */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
            What matters most to you? (Optional)
          </Typography>
          <Grid container spacing={2}>
            {priorityFactorOptions.map((factor) => (
              <Grid item xs={6} sm={3} key={factor}>
                <Button
                  variant={projectData.priorityFactors.includes(factor) ? 'contained' : 'outlined'}
                  fullWidth
                  onClick={() => {
                    setProjectData(prev => ({
                      ...prev,
                      priorityFactors: prev.priorityFactors.includes(factor)
                        ? prev.priorityFactors.filter(f => f !== factor)
                        : [...prev.priorityFactors, factor]
                    }));
                  }}
                >
                  {factor}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };

  // Render Step 4: Review & Create
  const renderStep4 = () => {
    // Client-facing readiness — the same idea contractors see as
    // "Ready to Quote / Needs More Details" in their marketplace, shown to
    // the client BEFORE posting so thin briefs stop reaching the marketplace.
    const answered = answeredBriefCount(projectData.details || {});
    const checklist = [
      {
        ok: (projectData.description || '').trim().length >= 60,
        label: 'A clear brief (a few sentences)'
      },
      {
        ok: projectData.photos.length > 0 || projectData.planFiles.length > 0,
        label: 'Photos or plans attached'
      },
      {
        ok: answered >= 2,
        label: 'Project details answered'
      },
      {
        ok: projectData.budgetApproach === 'specific' && parseFloat(projectData.budget) > 0,
        label: 'Budget shared (optional, but attracts serious quotations)'
      }
    ];
    const readyScore = checklist.filter((c) => c.ok).length;
    const readinessLabel = readyScore >= 3
      ? 'Ready to Quote'
      : readyScore >= 2
        ? 'Assessment Recommended'
        : 'Needs More Details';

    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h5" align="center" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
          Review Your Project
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Make sure your brief, location, and reference photos are clear before creating
        </Typography>

        {/* How contractors will see this listing */}
        <Alert
          severity={readyScore >= 3 ? 'success' : 'warning'}
          icon={readyScore >= 3 ? <CheckCircle /> : undefined}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Contractors will see this project as: {readinessLabel}
          </Typography>
          <Box component="ul" sx={{ m: 0.5, mb: 0, pl: 2.5, listStyle: 'none' }}>
            {checklist.map((c) => (
              <Typography
                key={c.label}
                component="li"
                variant="body2"
                sx={{ color: c.ok ? 'success.main' : 'text.secondary' }}
              >
                {c.ok ? '✓ ' : '○ '}{c.label}
              </Typography>
            ))}
          </Box>
          {readyScore < 3 && (
            <Typography variant="caption">
              Use Back to add the missing pieces — complete briefs get quotations without contractors having to chase you for details.
            </Typography>
          )}
        </Alert>

        {/* Project Summary */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Project Type
            </Typography>
            <Button size="small" onClick={() => setActiveStep(0)}>Edit</Button>
          </Box>
          <Chip
            label={projectType === 'quick' ? 'Quick Task' : 'Full Project'}
            color="primary"
            sx={{ mb: 1 }}
          />
          <Typography variant="body1">
            {effectiveTaskType(projectType, projectData)}
          </Typography>
        </Paper>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Project Brief
            </Typography>
            <Button size="small" onClick={() => setActiveStep(1)}>Edit</Button>
          </Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Project Title
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
            {projectData.name}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Location
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {typeof projectData.location === 'string' ? projectData.location : projectData.location?.address}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Your brief to contractors
          </Typography>
          {composeLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Writing a clear brief from your answers…
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={projectData.description}
            onChange={(e) => setProjectData(prev => ({ ...prev, description: e.target.value }))}
            helperText="This is exactly what contractors will read. Edit it freely."
            sx={{ mb: 1.5 }}
          />
          {composedBrief && projectData.description.trim() !== composedBrief.trim() && (
            <Paper
              variant="outlined"
              onClick={() => {
                if (!originalNote) setOriginalNote(projectData.description);
                setProjectData(prev => ({ ...prev, description: composedBrief }));
              }}
              sx={{ p: 2, mb: 2, cursor: 'pointer', borderColor: 'primary.main', '&:hover': { bgcolor: 'primary.50' } }}
            >
              <Typography variant="caption" color="primary" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AutoAwesome sx={{ fontSize: 14 }} /> AI-improved brief — click to use it
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                {composedBrief}
              </Typography>
            </Paper>
          )}
          {originalNote && originalNote.trim() && projectData.description.trim() !== originalNote.trim() && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Written with AI from your note: “{originalNote.length > 140 ? `${originalNote.slice(0, 140)}…` : originalNote}”
              <Button
                size="small"
                sx={{ ml: 1, textTransform: 'none' }}
                onClick={() => setProjectData(prev => ({ ...prev, description: originalNote }))}
              >
                Restore my original
              </Button>
            </Typography>
          )}
          {answered > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Project details
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                {structuredBriefRows(collectStructuredBrief()).map((row) => (
                  <Chip key={row.label} size="small" variant="outlined" label={`${row.label}: ${row.value}`} />
                ))}
              </Box>
            </>
          )}
          {projectData.planFiles.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Plans & Drawings
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                {projectData.planFiles.map((file, index) => (
                  <Chip key={`${file.name}-${index}`} size="small" icon={<Description />} label={file.name} />
                ))}
              </Box>
            </>
          )}
          {projectData.photos.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Reference Photos
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 1 }}>
                {photoPreviews.map((preview, index) => (
                  <Grid item xs={6} sm={4} md={3} key={`${preview.file.name}-review-${index}`}>
                    <Paper
                      variant="outlined"
                      sx={{ overflow: 'hidden', borderRadius: 1.5, aspectRatio: '1 / 1' }}
                    >
                      <Box
                        component="img"
                        src={preview.url}
                        alt={preview.file.name}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Budget & Timing
            </Typography>
            <Button size="small" onClick={() => setActiveStep(2)}>Edit</Button>
          </Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Budget
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {projectData.budgetApproach === 'specific' && projectData.budget
              ? `$${parseFloat(projectData.budget).toLocaleString()} ${projectData.currency}`
              : projectData.budgetApproach === 'guidance'
              ? 'Seeking guidance from contractors'
              : 'To be determined'}
          </Typography>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Timeline
          </Typography>
          <Typography variant="body1">
            {timelineOptions.find(opt => opt.value === projectData.timelineType)?.label}
            {projectData.timelineType === 'specific' && projectData.specificDate && ` (${projectData.specificDate})`}
          </Typography>
        </Paper>
        
        {/* Next Steps Preview */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            What happens next?
          </Typography>
          <Typography variant="body2">
            1. Your project will be created and saved<br />
            2. AI will match you with suitable contractors<br />
            3. Contractors will propose the work plan, milestones, and materials schedule<br />
            4. Review proposals and approve the plan before work starts
          </Typography>
        </Alert>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  };
  
  // Render Post-Creation Flow
  const renderPostCreation = () => {
    return (
      <>
        {photoUploadFailed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your project was created, but the reference photos could not be uploaded.
            Open your project page and use <strong>Upload Files</strong> to add them —
            contractors quote more accurately when they can see the site.
          </Alert>
        )}
        <PostCreationFlow
          projectId={createdProjectId}
          projectData={{
            ...projectData,
            projectType
          }}
          onClose={handleClose}
        />
      </>
    );
  };
  
  // Main render
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        <Box sx={{ position: 'relative' }}>
          
          {!showPostCreation ? (
            <>
              {/* Stepper */}
              <Box sx={{ mb: 4, mt: 2 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              
              {/* Step content */}
              <Box sx={{ minHeight: 400 }}>
                {activeStep === 0 && renderStep1()}
                {activeStep === 1 && renderStep2()}
                {activeStep === 2 && renderStep3()}
                {activeStep === 3 && renderStep4()}
              </Box>
              
              {/* Navigation buttons */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0 || loading}
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={loading || aiLoading}
                  endIcon={activeStep === 3 ? <CheckCircle /> : <ArrowForward />}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : activeStep === 3 ? (
                    (user ? 'Create Project' : 'Create free account & post')
                  ) : (
                    'Next'
                  )}
                </Button>
              </Box>
              {!user && activeStep === 3 && (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 1, color: 'text.secondary' }}>
                  Your brief is saved. Next you create a free account (name, email, phone) and it's posted to verified contractors.
                  {' '}Already have one? <Box component="a" href={`/login?next=${encodeURIComponent('/projects?create=1')}`} sx={{ color: 'primary.main' }}>Sign in</Box>.
                </Typography>
              )}
            </>
          ) : (
            renderPostCreation()
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedProjectWizard;
